import { Link, Outlet } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { SiteTag } from '@aitoken/ui/components/site';
import { SuccessBoard, useHomeRates } from '@/components/home/success-board';
import { Wordmark } from '@/components/wordmark';
import { demoQuote } from '@/lib/demo-quote';
import { publicModelsQuery, useSite } from '@/lib/queries';
import { useLocale } from '@/i18n';
import { LanguageSwitcher } from '@/i18n/language-switcher';
import { cn } from '@aitoken/ui/lib/utils';
import { useAuthT } from '@/i18n/messages/auth';
import { useCommonT } from '@/i18n/messages/common';

/**
 * 登录 / 注册 / 找回密码共用：左边森林绿的“舞台”放着首页那块成功率面板，右边是白纸上的表单。
 * 表单里的 shadcn Card 在这里去掉边框和阴影，直接落在白纸上。
 */
export function AuthLayout() {
  const site = useSite();
  const models = useQuery(publicModelsQuery);
  const q = demoQuote(models.data?.[0]);
  const rates = useHomeRates();
  const t = useAuthT();
  const tc = useCommonT();
  // 拉丁文字比汉字/韩文宽得多：放宽行宽、略收字号，让标语保持两行
  const locale = useLocale();
  const latin = locale === 'en' || locale === 'vi';
  return (
    <div className="flex min-h-svh flex-col bg-white text-ink">
      <div className="grid flex-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <aside className="dark field-ink relative hidden flex-col justify-between gap-10 bg-ink p-[var(--px)] text-white lg:flex">
          <Link
            to="/"
            aria-label={t('layout.home')}
            className="w-fit rounded-[4px] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold"
          >
            <Wordmark />
          </Link>
          <div className="flex flex-col gap-8">
            <p
              className={cn(
                'display-cjk text-balance',
                latin
                  ? 'max-w-[15em] text-[clamp(32px,2.9vw,44px)]'
                  : 'max-w-[11em] text-[clamp(36px,3.4vw,52px)]',
              )}
            >
              {t('layout.headline')}
            </p>
            <div className="h-[clamp(300px,24vw,360px)] max-w-[600px]">
              <SuccessBoard quote={q} rates={rates} />
            </div>
          </div>
          <p className="text-[15px] text-[#c9c2b6]">
            {t('layout.footnote', {
              brand: 'aitoken.tools',
              site: tc(site.site_id === 'cn' ? 'site.cn' : 'site.global'),
              currency: site.currency,
            })}
          </p>
        </aside>

        <div className="flex min-w-0 flex-col">
          <header className="flex items-center justify-between gap-4 p-[var(--px)] pb-0">
            <Link
              to="/"
              aria-label={t('layout.home')}
              className="rounded-[4px] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink lg:invisible"
            >
              <Wordmark tone="dark" />
            </Link>
            <div className="flex min-w-0 items-center gap-1">
              {/* 窄屏只留图标，避免与站点标签挤出屏幕 */}
              <LanguageSwitcher className="text-[#6b645a] hover:text-ink max-sm:px-2 max-sm:[&>span]:sr-only" />
              <SiteTag
                site={site.site_id}
                className="h-7 rounded-[4px] border-transparent bg-frame text-[13px]"
              />
            </div>
          </header>
          <main
            id="main"
            className="flex flex-1 items-start justify-center px-[var(--px)] pt-10 pb-16 sm:items-center sm:pt-6"
          >
            <div className="w-full max-w-[400px] [&_[data-slot=card]]:gap-7 [&_[data-slot=card]]:border-0 [&_[data-slot=card]]:bg-transparent [&_[data-slot=card]]:py-0 [&_[data-slot=card]]:shadow-none [&_[data-slot=card-content]]:px-0 [&_[data-slot=card-description]]:text-[15px] [&_[data-slot=card-footer]]:px-0 [&_[data-slot=card-header]]:px-0 [&_[data-slot=card-title]]:font-display [&_[data-slot=card-title]]:text-[36px] [&_[data-slot=card-title]]:leading-[1.05] [&_[data-slot=card-title]]:font-bold [&_[data-slot=card-title]]:tracking-[-0.02em] [&_[data-slot=field-label]]:text-[15px] [&_input]:h-11 [&_input]:text-base [&_button[type=submit]]:h-11 [&_button[type=submit]]:text-base">
              <Outlet />
            </div>
          </main>
          <footer className="flex flex-wrap items-center justify-between gap-4 px-[var(--px)] pb-6 text-[15px] text-[#6b645a]">
            <span className="flex flex-wrap gap-x-5 gap-y-2">
              <Link to="/docs" className="hover:text-ink hover:underline hover:underline-offset-4">
                {t('layout.docs')}
              </Link>
              <Link to="/models" className="hover:text-ink hover:underline hover:underline-offset-4">
                {t('layout.models')}
              </Link>
            </span>
            {site.contact.email ? (
              <a
                href={`mailto:${site.contact.email}`}
                className="hover:text-ink hover:underline hover:underline-offset-4"
              >
                {site.contact.email}
              </a>
            ) : null}
          </footer>
        </div>
      </div>
    </div>
  );
}
